import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';

// "idle" is a lazy client with no socket, before its first use or after
// closing one nothing needed, so it never means the connection failed
export type ChatConnection = 'idle' | 'connecting' | 'open' | 'reconnecting';

type ConnectionStore = {
  get: () => ChatConnection;
  subscribe: (onChange: () => void) => () => void;
};

// The client says "connecting" both before its first open and after a drop, so
// only whether it has opened since it last started afresh tells the two apart.
// It starts afresh when it opens the socket itself (an idle lazy client, or one
// whose socket closed with nothing to reconnect for): only a drop or a failed
// connect carries an error. After a reconnect the client can leave its own state on "connecting"
// with the socket already open (a send made while it was down re-announces
// "connecting"), so while reconnecting the socket itself is checked, or the
// chat says "Reconnecting…" for good.
const RECHECK_MS = 1000;

type ConnectionSource = {
  connectionState: {
    subscribe: (observer: { next: (value: { state: string; error: unknown }) => void }) => unknown;
  };
  connection: { state: string } | null;
};

export function trackConnection(client: ConnectionSource): ConnectionStore {
  let hasOpened = false;
  let current: ChatConnection = 'connecting';
  let recheck: ReturnType<typeof setInterval> | undefined;
  const listeners = new Set<() => void>();
  const isOpen = () => client.connection?.state === 'open';
  const set = (next: ChatConnection) => {
    if (next === 'reconnecting') {
      recheck ??= setInterval(() => {
        if (isOpen()) {
          update(true);
        }
      }, RECHECK_MS);
    } else if (recheck !== undefined) {
      clearInterval(recheck);
      recheck = undefined;
    }
    if (next !== current) {
      current = next;
      listeners.forEach((listener) => listener());
    }
  };
  const update = (open: boolean) => {
    hasOpened ||= open;
    set(open ? 'open' : hasOpened ? 'reconnecting' : 'connecting');
  };
  client.connectionState.subscribe({
    next: ({ state, error }) => {
      if (state === 'idle') {
        hasOpened = false;
        set('idle');
        return;
      }
      if (state === 'connecting' && error == null) {
        hasOpened = false;
      }
      update(state === 'pending' || isOpen());
    },
  });
  return {
    get: () => current,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const ChatConnectionContext = createContext<ChatConnection>('open');

export function ChatConnectionProvider({
  store,
  children,
}: {
  store: ConnectionStore;
  children?: ReactNode;
}) {
  const connection = useSyncExternalStore(store.subscribe, store.get, store.get);
  return (
    <ChatConnectionContext.Provider value={connection}>{children}</ChatConnectionContext.Provider>
  );
}

export function useChatConnection() {
  return useContext(ChatConnectionContext);
}
